import { useState } from "react";
import { toast } from "sonner";
import { User, Mail, Shield, Boxes, Save, ExternalLink, Link2 } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, GlassCard, Chip, Disclaimer } from "../components/ui";
import { DISCLAIMER } from "../constants";
import { GoogleAuthButton } from "../components/GoogleAuthButton";

const ECOSYSTEM = [
  { name: "ArcaCentum.ai", desc: "AI App Builder", href: "https://arcacentum.ai", live: true },
  { name: "ArcaCapitalis", desc: "Business Growth & Consulting", href: "https://arcacapitalis.com", live: true },
  { name: "ArcaCentum Credit", desc: "AI Business & Personal Credit", href: "/credit", live: true, current: true },
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await api.put("/auth/profile", { name }); await refreshUser(); toast.success("Profile updated"); }
    catch { toast.error("Could not update profile"); }
    finally { setSaving(false); }
  };

  const googleLinked = (user?.auth_providers || []).includes("google");
  const unlinkGoogle = async () => {
    try { await api.post("/auth/unlink/google"); await refreshUser(); toast.success("Google account unlinked"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not unlink"); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader label="Account" title="Settings" subtitle="Manage your profile, plan, and ArcaCentum ecosystem access." />

      <GlassCard className="p-6">
        <h3 className="cc-heading text-lg font-500 text-white mb-5 flex items-center gap-2"><User size={18} className="text-blue-300" /> Profile</h3>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm mt-2" data-testid="settings-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Email</label>
            <div className="cc-input w-full rounded-xl px-4 py-2.5 text-sm mt-2 flex items-center gap-2 text-slate-400">
              <Mail size={15} /> {user?.email}
              {user?.verified
                ? <Chip tone="green">Verified</Chip>
                : <button onClick={async () => { try { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); } catch { toast.error("Could not send email"); } }} data-testid="resend-verification" className="ml-auto text-xs text-blue-300 hover:text-blue-200">Resend verification</button>}
            </div>
          </div>
          <button onClick={save} disabled={saving} className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-500 disabled:opacity-60" data-testid="settings-save">
            <Save size={15} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="cc-heading text-lg font-500 text-white mb-2 flex items-center gap-2"><Link2 size={18} className="text-emerald-300" /> Connected accounts</h3>
        <p className="text-sm text-slate-400 mb-4">Link a sign-in provider for faster, password-free access.</p>
        <div className="flex items-center justify-between rounded-xl cc-surface border border-white/[0.05] px-4 py-3">
          <div>
            <div className="text-sm text-white">Google</div>
            <div className="text-xs text-slate-500">{googleLinked ? (user?.linked_accounts?.google?.email || "Connected") : "Not connected"}</div>
          </div>
          {googleLinked ? (
            <button onClick={unlinkGoogle} data-testid="unlink-google"
              className="rounded-xl border border-white/12 px-4 py-2 text-sm text-slate-300 cc-nav-item">Unlink</button>
          ) : (
            <div className="w-52"><GoogleAuthButton mode="link" label="Link Google" testid="link-google" onDone={refreshUser} /></div>
          )}
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white capitalize text-lg cc-heading">{user?.plan || "free"} plan</div>
            <p className="text-sm text-slate-400 mt-0.5">Manage billing and upgrade your plan.</p>
          </div>
          <a href="/credit/app/billing" className="rounded-xl border border-white/12 px-4 py-2.5 text-sm text-white cc-nav-item">View Plans</a>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="cc-heading text-lg font-500 text-white mb-2 flex items-center gap-2"><Boxes size={18} className="text-blue-300" /> ArcaCentum Ecosystem</h3>
        <p className="text-sm text-slate-400 mb-4">One account across all ArcaCentum products.</p>
        <div className="space-y-2.5">
          {ECOSYSTEM.map((p) => (
            <a key={p.name} href={p.href} target={p.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
              className="flex items-center justify-between rounded-xl cc-surface border border-white/[0.05] px-4 py-3 cc-nav-item">
              <div>
                <div className="text-sm text-white flex items-center gap-2">{p.name} {p.current && <Chip tone="blue">Current</Chip>}</div>
                <div className="text-xs text-slate-500">{p.desc}</div>
              </div>
              <ExternalLink size={15} className="text-slate-500" />
            </a>
          ))}
        </div>
      </GlassCard>

      <Disclaimer text={DISCLAIMER} />
    </div>
  );
}
